import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { paymentApi, TransactionBillCorrectionLog } from '../api/paymentApi';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Tiền mặt',
  BANK_TRANSFER: 'Chuyển khoản',
};

interface TransactionReportHistoryTabProps {
  buildingId: string;
  fromDate: string;
  toDate: string;
  onOpenBill?: (billId: string) => void;
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  const day = d.toLocaleDateString('vi-VN');
  const time = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${time} ${day}`;
};

const formatDateOnly = (iso: string) => new Date(iso).toLocaleDateString('vi-VN');

export const TransactionReportHistoryTab: React.FC<TransactionReportHistoryTabProps> = ({
  buildingId,
  fromDate,
  toDate,
  onOpenBill,
}) => {
  const [logs, setLogs] = useState<TransactionBillCorrectionLog[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const data = await paymentApi.getTransactionCorrectionHistory({
        buildingId,
        fromDate,
        toDate,
      });
      setLogs(data || []);
    } catch {
      toast.error('Không tải được lịch sử chỉnh sửa');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const describeChanges = (log: TransactionBillCorrectionLog) => {
    if (log.activityType === 'VOUCHER_SETTLEMENT') {
      const fmt = (n: number) => Math.round(n).toLocaleString('vi-VN');
      const action =
        log.voucherAction === 'SETTLED' ? 'Đánh dấu đã thu từ đối tác' : 'Bỏ đánh dấu đối soát';
      const period = log.billingPeriod ? ` · kỳ ${log.billingPeriod}` : '';
      return `${action}: ${log.voucherCounterparty || '—'} · ${fmt(log.voucherAmount ?? 0)} đ${period}`;
    }
    const parts: string[] = [];
    if (log.previousPaymentMethod && log.newPaymentMethod) {
      parts.push(
        `Hình thức: ${PAYMENT_METHOD_LABELS[log.previousPaymentMethod] || log.previousPaymentMethod} → ${PAYMENT_METHOD_LABELS[log.newPaymentMethod] || log.newPaymentMethod}`,
      );
    }
    if (log.previousPaidAt && log.newPaidAt) {
      parts.push(`Ngày TT: ${formatDateOnly(log.previousPaidAt)} → ${formatDateOnly(log.newPaidAt)}`);
    }
    if (
      log.previousAmountReceived != null &&
      log.newAmountReceived != null &&
      log.previousAmountReceived !== log.newAmountReceived
    ) {
      const fmt = (n: number) => Math.round(n).toLocaleString('vi-VN');
      parts.push(`Thực thu: ${fmt(log.previousAmountReceived)} → ${fmt(log.newAmountReceived)} đ`);
    }
    if (
      log.previousOverpaymentAmount != null &&
      log.newOverpaymentAmount != null &&
      log.previousOverpaymentAmount !== log.newOverpaymentAmount
    ) {
      const fmt = (n: number) => Math.round(n).toLocaleString('vi-VN');
      parts.push(`Thu dư: ${fmt(log.previousOverpaymentAmount)} → ${fmt(log.newOverpaymentAmount)} đ`);
    }
    if (log.changeSummary?.trim()) {
      parts.push(log.changeSummary.trim());
    }
    return parts.join(' · ') || '—';
  };

  if (!buildingId) {
    return <p className="text-sm text-gray-500 py-8 text-center">Chọn chung cư và tìm kiếm để xem lịch sử.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Lịch sử chỉnh sửa giao dịch, huỷ phiếu thu phí và đối soát voucher xe vãng lai trong khoảng đã chọn (
        {logs.length} bản ghi).
      </p>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
        </div>
      ) : logs.length === 0 ? (
        <p className="text-center text-gray-500 py-12 text-sm">Chưa có chỉnh sửa trong khoảng thời gian này.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left whitespace-nowrap">Thời gian</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Người thực hiện</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Phiếu thu</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Căn hộ</th>
                <th className="px-3 py-2 text-left">Thay đổi</th>
                <th className="px-3 py-2 text-left">Ghi chú</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{log.createdByName}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium">
                    {log.activityType === 'VOUCHER_SETTLEMENT' ? (
                      'Voucher XL'
                    ) : log.billId && onOpenBill && (log.receiptNumber || log.billCode) ? (
                      <button
                        type="button"
                        onClick={() => onOpenBill(log.billId!)}
                        className="text-cyan-600 hover:text-cyan-800 underline font-semibold"
                      >
                        {log.receiptNumber || log.billCode}
                      </button>
                    ) : (
                      log.receiptNumber || log.billCode || '—'
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{log.apartmentCode}</td>
                  <td className="px-3 py-2">{describeChanges(log)}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={log.notes || ''}>
                    {log.notes || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransactionReportHistoryTab;
